/**
 * Client for `/api/ingest` — sales workbook upload.
 *
 * Upload uses XMLHttpRequest rather than `fetch` because it is the only
 * browser API that reports upload progress, and these files run to 100+ MB.
 * The error envelope is parsed the same way `request` parses it.
 */

import { ApiError, getCsrfToken, request } from './api'

export type UploadOutcome = 'queued' | 'requeued' | 'duplicate' | 'rejected'

export interface UploadResult {
  readonly filename: string
  readonly outcome: UploadOutcome
  readonly message: string
  readonly file_id: number | null
}

export function uploadWorkbooks(
  files: readonly File[],
  onProgress: (fraction: number) => void,
): Promise<UploadResult[]> {
  const form = new FormData()
  for (const file of files) form.append('files', file, file.name)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/ingest/files')
    xhr.setRequestHeader('Accept', 'application/json')
    const token = getCsrfToken()
    if (token !== null) xhr.setRequestHeader('X-CSRF-Token', token)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total)
    }
    xhr.onerror = () => {
      reject(new ApiError(0, 'network_error', 'Upload failed: network error'))
    }
    xhr.onload = () => {
      let body: unknown
      try {
        body = JSON.parse(xhr.responseText) as unknown
      } catch {
        body = null
      }
      if (xhr.status >= 200 && xhr.status < 300 && Array.isArray(body)) {
        resolve(body as UploadResult[])
        return
      }
      const error = (body as { error?: { code?: string; message?: string } } | null)?.error
      reject(
        new ApiError(
          xhr.status,
          error?.code ?? 'error',
          error?.message ?? `Upload returned ${String(xhr.status)}`,
        ),
      )
    }
    xhr.send(form)
  })
}

export function retryIngest(fileId: number): Promise<UploadResult> {
  return request<UploadResult>(`/api/ingest/files/${String(fileId)}/retry`, { method: 'POST' })
}
